import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import DashboardLayout from "@/components/DashboardLayout";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, User, Building2, Calendar, Star, ClipboardList,
  MessageCircle, Send, ClipboardCheck, TrendingUp, School, AlertTriangle,
} from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("ar-SA"); } catch { return "—"; }
};

/**
 * Deep read-only detail view of one student for the academic faculty.
 * Sections: identity → training placement → attendance strip → evaluations
 *         → tasks → faculty comments (write) → student weekly reports.
 */
export default function FacultyStudentDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const { data } = await api.get(`/faculty/students/${id}/detail`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const activePlacement = useMemo(() => (
    (data?.placements || []).find((p) => ["accepted", "assigned"].includes(p.status)) || (data?.placements || [])[0]
  ), [data]);

  const submitComment = async () => {
    const body = comment.trim();
    if (!body) { toast.error("اكتب تعليقًا أولًا"); return; }
    setPosting(true);
    try {
      await api.post(`/faculty/students/${id}/comments`, { body });
      toast.success("تم إرسال التعليق للطالب");
      setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPosting(false); }
  };

  if (loading) return <DashboardLayout><div className="grid place-items-center py-24"><Loader2 className="h-8 w-8 animate-spin text-teal-700" /></div></DashboardLayout>;
  if (error) return <DashboardLayout><div className="p-4 rounded-2xl border border-red-200 bg-red-50 text-red-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" />{error}</div></DashboardLayout>;
  if (!data) return null;

  const { student, placements, tasks, comments, reports } = data;

  return (
    <DashboardLayout>
      <div data-testid="faculty-student-detail">
        <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-sm text-teal-700 font-semibold hover:text-teal-800 mb-6" data-testid="back-to-faculty">
          <ArrowRight className="h-4 w-4" /> رجوع لطلابي
        </Link>

        {/* Identity */}
        <section className="rounded-2xl bg-gradient-to-l from-teal-700 to-teal-800 text-white p-4 mb-8 card-shadow" data-testid="identity-card">
          <div className="flex items-start gap-4">
            <div className="h-16 w-16 rounded-full bg-white/10 grid place-items-center text-white font-heading font-black text-2xl">
              {student.name?.[0] || <User className="h-6 w-6" />}
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="font-heading font-black text-2xl">{student.name}</h1>
              <div className="mt-1 text-teal-100 text-sm flex flex-wrap gap-x-3 gap-y-1">
                {student.university && <span className="inline-flex items-center gap-1"><School className="h-3.5 w-3.5" />{student.university}</span>}
                {student.college && <span>· {student.college}</span>}
                {student.major && <span>· {student.major}</span>}
                {student.gpa && <span className="arabic-numerals">· المعدل {student.gpa}</span>}
              </div>
              <div className="mt-1 text-teal-100 text-xs">{student.email}</div>
            </div>
          </div>
        </section>

        {/* Placement + Attendance + Evaluations */}
        {activePlacement ? (
          <section className="grid lg:grid-cols-3 gap-5 mb-8">
            <PlacementCard placement={activePlacement} />
            <AttendanceCard attendance={activePlacement.attendance} />
            <EvalsCard placement={activePlacement} />
          </section>
        ) : (
          <div className="mb-8 p-4 rounded-2xl border border-dashed border-slate-300 bg-white text-sm text-slate-600">
            لا يوجد تدريب تعاوني نشط لهذا الطالب حالياً.
          </div>
        )}

        {/* Tasks */}
        <section id="reports" className="mb-8 scroll-mt-6">
          <SectionHeader icon={ClipboardList} title="المهام المسنَدة" count={tasks.length} testid="tasks-section" />
          {tasks.length === 0 ? (
            <EmptyStrip text="لم تُكلَّف بمهام حتى الآن." />
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {tasks.map((t) => (
                <div key={t.id} className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`task-${t.id}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="font-heading font-bold text-slate-900">{t.title}</div>
                      {t.association_name && <div className="text-xs text-slate-500 mt-0.5">{t.association_name}</div>}
                    </div>
                    <StatusChip status={t.status} />
                  </div>
                  {t.description && <p className="mt-2 text-sm text-slate-700 line-clamp-3">{t.description}</p>}
                  <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                    {t.due_date && <span className="inline-flex items-center gap-1"><Calendar className="h-3.5 w-3.5" />{fmtDate(t.due_date)}</span>}
                    {t.score != null && (
                      <span className="inline-flex items-center gap-1 font-bold text-amber-700 arabic-numerals">
                        <Star className="h-3.5 w-3.5 fill-amber-500 text-amber-500" /> {t.score}/100
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Weekly reports */}
        <section className="mb-8">
          <SectionHeader icon={TrendingUp} title="التقارير الأسبوعية من الطالب" count={reports.length} testid="reports-section" />
          {reports.length === 0 ? (
            <EmptyStrip text="لم يُرسل الطالب أي تقرير أسبوعي بعد." />
          ) : (
            <div className="space-y-3">
              {reports.map((r) => (<ReportRow key={r.id} report={r} />))}
            </div>
          )}
        </section>

        {/* Faculty comment box + history */}
        <section id="comments" className="mb-4 scroll-mt-6">
          <SectionHeader icon={MessageCircle} title="تعليقاتي على الطالب" count={comments.length} testid="comments-section" />
          <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="comment-box">
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="اكتب توجيهاً أو ملاحظة أكاديمية للطالب — سيتلقّاها في صفحة تقاريره."
              className="w-full rounded-lg border border-slate-300 px-4 py-3 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none text-sm"
              data-testid="comment-body"
            />
            <div className="mt-2 flex justify-end">
              <button
                onClick={submitComment}
                disabled={posting}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white font-semibold hover:bg-teal-600 disabled:opacity-60"
                data-testid="post-comment-btn"
              >
                {posting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال التعليق
              </button>
            </div>
          </div>

          {comments.length > 0 && (
            <div className="mt-4 space-y-2">
              {comments.map((c) => (
                <div key={c.id} className="p-4 rounded-xl bg-cream-50 border border-cream-200 text-sm">
                  <div className="text-xs text-slate-500 mb-1">
                    <span className="font-bold text-teal-700">{c.faculty_name}</span> · {fmtDate(c.created_at)}
                  </div>
                  <div className="text-slate-800 whitespace-pre-line">{c.body}</div>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </DashboardLayout>
  );
}

function SectionHeader({ icon: Icon, title, count, testid }) {
  return (
    <div className="mb-3 flex items-center gap-2" data-testid={`${testid}-header`}>
      <Icon className="h-5 w-5 text-teal-700" />
      <h2 className="font-heading font-black text-slate-900">{title}</h2>
      <span className="ms-auto text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold arabic-numerals">{count}</span>
    </div>
  );
}

function EmptyStrip({ text }) {
  return <div className="p-4 rounded-xl border border-dashed border-slate-300 bg-white text-sm text-slate-500">{text}</div>;
}

function StatusChip({ status }) {
  const map = {
    done:      { label: "مكتملة", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    submitted: { label: "بانتظار التقييم", cls: "bg-sky-50 text-sky-700 border-sky-200" },
    open:      { label: "مفتوحة", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  };
  const m = map[status] || { label: status || "—", cls: "bg-slate-100 text-slate-600 border-slate-200" };
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold border ${m.cls}`}>{m.label}</span>;
}

function PlacementCard({ placement }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="placement-card">
      <div className="flex items-center gap-2 mb-3">
        <Building2 className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">التدريب الحالي</div>
      </div>
      <div className="font-heading font-black text-slate-900">{placement.opportunity_title || "—"}</div>
      <div className="mt-3 space-y-2 text-sm">
        <Row label="الجمعية" value={placement.association_name} />
        <Row label="الموجّه" value={placement.mentor_name} />
        <Row label="الفترة" value={placement.training_start ? `${fmtDate(placement.training_start)} — ${fmtDate(placement.training_end)}` : "—"} />
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between items-baseline gap-2 border-b border-slate-100 last:border-0 pb-1.5 last:pb-0">
      <span className="text-xs text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800 text-sm text-end">{value || "—"}</span>
    </div>
  );
}

function AttendanceCard({ attendance }) {
  const a = attendance || {};
  const present = Number(a.present_days || 0);
  const absent = Number(a.absent_days || 0);
  const total = present + absent;
  const pct = total > 0 ? Math.round((present / total) * 100) : 0;

  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="attendance-card">
      <div className="flex items-center gap-2 mb-3">
        <Calendar className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">الحضور والغياب</div>
      </div>
      {total === 0 ? (
        <div className="text-sm text-slate-500 py-2">لم تُسجَّل بيانات حضور بعد.</div>
      ) : (
        <>
          <div className="flex items-baseline gap-1">
            <span className="text-2xl font-heading font-black text-emerald-700 arabic-numerals">{pct}%</span>
            <span className="text-xs text-slate-500">نسبة الحضور</span>
          </div>
          {/* Daily strip: green = present, red = absent */}
          <div className="mt-3 flex flex-wrap gap-1" data-testid="daily-strip">
            {Array.from({ length: present }).map((_, i) => (
              <span key={`p-${i}`} className="h-4 w-2 rounded-sm bg-emerald-500" title="حضور" />
            ))}
            {Array.from({ length: absent }).map((_, i) => (
              <span key={`a-${i}`} className="h-4 w-2 rounded-sm bg-rose-400" title="غياب" />
            ))}
          </div>
          <div className="mt-4 grid grid-cols-3 gap-2 text-center text-xs">
            <div className="p-2 rounded-lg bg-emerald-50 border border-emerald-100"><div className="font-bold text-emerald-700 arabic-numerals">{present}</div><div className="text-slate-500">حضور</div></div>
            <div className="p-2 rounded-lg bg-rose-50 border border-rose-100"><div className="font-bold text-rose-700 arabic-numerals">{absent}</div><div className="text-slate-500">غياب</div></div>
            <div className="p-2 rounded-lg bg-teal-50 border border-teal-100"><div className="font-bold text-teal-700 arabic-numerals">{a.total_hours || 0}</div><div className="text-slate-500">ساعة</div></div>
          </div>
          {a.notes && <div className="mt-3 text-xs text-slate-600 bg-slate-50 rounded-lg p-2">{a.notes}</div>}
        </>
      )}
    </div>
  );
}

function EvalsCard({ placement }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid="evals-card">
      <div className="flex items-center gap-2 mb-3">
        <ClipboardCheck className="h-5 w-5 text-teal-700" />
        <div className="text-xs font-bold uppercase tracking-widest text-teal-700">تقييمات الطالب</div>
      </div>
      <EvalBlock title="الجمعية" score={placement.association_score} notes={placement.association_notes} tint="from-emerald-50 to-emerald-100 text-emerald-800 border-emerald-200" />
      <div className="mt-3">
        <EvalBlock title="الموجّه" score={placement.mentor_score} notes={placement.mentor_notes} tint="from-indigo-50 to-indigo-100 text-indigo-800 border-indigo-200" />
      </div>
    </div>
  );
}

function EvalBlock({ title, score, notes, tint }) {
  return (
    <div className={`rounded-xl border bg-gradient-to-l ${tint} p-3`}>
      <div className="flex items-center justify-between">
        <span className="text-sm font-bold">{title}</span>
        {score != null ? (
          <span className="inline-flex items-center gap-1 font-heading font-black arabic-numerals">
            <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
            {score}/100
          </span>
        ) : <span className="text-xs opacity-70">لم يُقيَّم بعد</span>}
      </div>
      {notes && <div className="mt-1 text-xs whitespace-pre-line opacity-90">{notes}</div>}
    </div>
  );
}

function ReportRow({ report }) {
  return (
    <div className="p-4 rounded-2xl bg-white border border-cream-200 card-shadow" data-testid={`report-${report.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-slate-900">{report.task_title}</div>
          <div className="text-xs text-slate-500 mt-0.5">
            الأسبوع: {fmtDate(report.week_of)} · أُرسل في {fmtDate(report.created_at)}
          </div>
        </div>
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-teal-50 border border-teal-200 text-teal-800 text-xs font-bold arabic-numerals">
          <TrendingUp className="h-3.5 w-3.5" /> {report.progress_percent}%
        </span>
      </div>
      {/* Progress bar */}
      <div className="mt-3 h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className="h-full bg-gradient-to-l from-teal-500 to-teal-700 transition-all" style={{ width: `${Math.max(0, Math.min(100, report.progress_percent || 0))}%` }} />
      </div>
      {report.challenges && (
        <div className="mt-3 rounded-lg bg-rose-50 border border-rose-100 p-3 text-sm">
          <div className="text-[11px] font-bold text-rose-700 mb-1">التحديات التي واجهها</div>
          <div className="text-slate-800 whitespace-pre-line">{report.challenges}</div>
        </div>
      )}
      {report.actions_taken && (
        <div className="mt-2 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-sm">
          <div className="text-[11px] font-bold text-emerald-700 mb-1">كيف تعامل معها</div>
          <div className="text-slate-800 whitespace-pre-line">{report.actions_taken}</div>
        </div>
      )}
    </div>
  );
}
